import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiError } from "@/lib/api";

const COUNTRIES = [
  "Russia",
  "Georgia",
  "Uzbekistan",
  "Kazakhstan",
  "Kyrgyzstan",
  "Philippines",
  "Bangladesh",
  "Nepal",
  "Open to recommendation",
];

export default function RegisterPage() {
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    neet_score_type: "expected",
    neet_score: "",
    preferred_country: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.preferred_country) {
      toast.error("Please pick a preferred country");
      return;
    }
    const score = Number(form.neet_score);
    if (Number.isNaN(score) || score < 0 || score > 720) {
      toast.error("NEET score must be between 0 and 720");
      return;
    }
    setSubmitting(true);
    try {
      let utm = {};
      try {
        const stored = localStorage.getItem("medix_utm");
        if (stored) utm = JSON.parse(stored);
      } catch {
        utm = {};
      }
      const cta_variant = localStorage.getItem("medix_cta_variant") || undefined;
      // Pull the tenant slug stashed by TenantLandingPage (`/t/:slug`) so the
      // lead lands in the right tenant's bucket. Falls back to undefined →
      // backend uses the default tenant.
      let tenantSlug;
      try {
        tenantSlug = sessionStorage.getItem("medadmits_tenant_slug") || undefined;
      } catch {
        tenantSlug = undefined;
      }
      const { data } = await api.post("/leads/register", {
        ...form,
        neet_score: score,
        ...utm,
        ...(cta_variant ? { cta_variant } : {}),
        ...(tenantSlug ? { tenant_slug: tenantSlug } : {}),
      });
      toast.success("Registered. Let's start your aptitude test!");
      nav(`/quiz/${data.lead_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A2540]"
            data-testid="back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">
            Step 1 of 2 &middot; Register
          </div>
        </div>
        <div className="h-1 w-1/3 bg-[#0A2540]" />
      </header>

      <main className="mx-auto grid max-w-5xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-12 lg:gap-14 lg:py-16">
        <aside className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">
            Free Registration
          </div>
          <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-[#0A2540] sm:text-4xl">
            Tell us about your MBBS journey.
          </h1>
          <p className="mt-4 text-slate-600">
            We&rsquo;ll match you with the right university and scholarship
            based on your NEET performance and country preference.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-600">
            {[
              "60-second registration",
              "Refresh-safe 10-minute aptitude test",
              "Scholarship eligibility within 48 hrs",
              "1:1 counselling — no spam calls",
            ].map((p) => (
              <li key={p} className="flex items-start gap-2.5">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#0A2540]" />
                {p}
              </li>
            ))}
          </ul>
        </aside>

        <form
          onSubmit={handleSubmit}
          className="lg:col-span-7 fade-up rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
          data-testid="register-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="name" className="text-sm">
                Full Name
              </Label>
              <Input
                id="name"
                required
                placeholder="Aanya Sharma"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="mt-1.5 h-12"
                data-testid="register-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="aanya@email.com"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-1.5 h-12"
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-sm">
                Phone Number
              </Label>
              <Input
                id="phone"
                required
                placeholder="+91 98XXXXXXXX"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className="mt-1.5 h-12"
                data-testid="register-phone"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-sm">NEET Score</Label>
              <RadioGroup
                value={form.neet_score_type}
                onValueChange={(v) => update("neet_score_type", v)}
                className="mt-2 flex gap-3"
              >
                <label
                  className={`flex flex-1 cursor-pointer items-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors ${
                    form.neet_score_type === "expected"
                      ? "border-[#0A2540] bg-[#0A2540]/5 text-[#0A2540]"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="score-type-expected"
                >
                  <RadioGroupItem value="expected" />
                  Expected
                </label>
                <label
                  className={`flex flex-1 cursor-pointer items-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors ${
                    form.neet_score_type === "actual"
                      ? "border-[#0A2540] bg-[#0A2540]/5 text-[#0A2540]"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="score-type-actual"
                >
                  <RadioGroupItem value="actual" />
                  Actual
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="score" className="text-sm">
                Score (out of 720)
              </Label>
              <Input
                id="score"
                type="number"
                required
                min={0}
                max={720}
                placeholder="450"
                value={form.neet_score}
                onChange={(e) => update("neet_score", e.target.value)}
                className="mt-1.5 h-12"
                data-testid="register-score"
              />
            </div>

            <div>
              <Label className="text-sm">Preferred Country</Label>
              <Select
                value={form.preferred_country}
                onValueChange={(v) => update("preferred_country", v)}
              >
                <SelectTrigger
                  className="mt-1.5 h-12"
                  data-testid="register-country-trigger"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`country-${c}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="mt-8 h-12 w-full bg-[#0A2540] text-base font-semibold text-white hover:bg-[#1E3A5F]"
            data-testid="register-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Registering…
              </>
            ) : (
              <>
                Continue to Test
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <p className="mt-4 text-center text-xs text-slate-500">
            By continuing you agree to our terms &amp; privacy policy. We never
            share data with universities without consent.
          </p>
        </form>
      </main>
    </div>
  );
}
